import React, { useState, useEffect } from 'react';
import { insightsAPI, entryAPI } from '../services/api';
import { Bar, Line } from 'react-chartjs-2';
import { handleApiError } from '../utils/errorHandler';

const Insights = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('30');
  const [correlationData, setCorrelationData] = useState(null);
  const [trendData, setTrendData] = useState(null);

  useEffect(() => {
    fetchInsights();
    fetchAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeRange]);

  const fetchInsights = async () => {
    try {
      const response = await insightsAPI.get();
      if (response.data.message) {
        // Not enough data message
        setInsights({ message: response.data.message });
      } else if (response.data.insights) {
        // OpenAI insights
        setInsights({ insights: response.data.insights });
      } else if (response.data.error) {
        setError(response.data.error);
      }
    } catch (error) {
      handleApiError(error, 'Error fetching insights');
      setError('Unable to load insights. Please try again later.');
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      // Get entries for the selected time range
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - parseInt(selectedTimeRange));
      
      // Fetch entries for future use - currently using mock data below
      await entryAPI.getAll({
        start_date: daysAgo.toISOString().split('T')[0]
      });
      // const entries = response.data; // Will be used when replacing mock data
      
      // Generate correlation data (pain vs food types)
      const foodCategories = ['Dairy', 'Gluten', 'Spicy', 'High Fiber', 'Processed'];
      const correlationChartData = {
        labels: foodCategories,
        datasets: [
          {
            label: 'Average Pain Level',
            data: [6.2, 7.1, 8.3, 5.4, 6.8], // Mock data - would be calculated from real entries
            backgroundColor: 'rgba(239, 68, 68, 0.8)',
            borderColor: 'rgba(239, 68, 68, 1)',
            borderWidth: 2,
          },
        ],
      };
      
      // Generate trend data
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - i));
        return date.toLocaleDateString('en-US', { weekday: 'short' });
      });
      
      const trendChartData = {
        labels: last7Days,
        datasets: [
          {
            label: 'Pain Level',
            data: [4, 6, 3, 7, 5, 4, 3], // Mock data
            borderColor: 'rgb(239, 68, 68)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            tension: 0.4,
          },
          {
            label: 'Bloating',
            data: [3, 5, 2, 6, 4, 3, 2], // Mock data
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            tension: 0.4,
          },
          {
            label: 'Mood (1-10)',
            data: [7, 5, 8, 4, 6, 7, 8], // Mock data
            borderColor: 'rgb(16, 185, 129)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            tension: 0.4,
          },
        ],
      };
      
      setCorrelationData(correlationChartData);
      setTrendData(trendChartData);
      setLoading(false);
    } catch (error) {
      handleApiError(error, 'Error fetching analytics data');
      setLoading(false);
    }
  };

  // Unused helper functions - kept for future use
  // const getInsightIcon = (type) => {
  //   switch (type) {
  //     case 'pattern': return '🔍';
  //     case 'trend': return '📈';
  //     case 'trigger': return '⚠️';
  //     case 'recommendation': return '💡';
  //     default: return '🧠';
  //   }
  // };

  // const getConfidenceColor = (confidence) => {
  //   if (confidence >= 0.8) return 'text-green-600 bg-green-100';
  //   if (confidence >= 0.6) return 'text-yellow-600 bg-yellow-100';
  //   return 'text-red-600 bg-red-100';
  // };

  // const getConfidenceText = (confidence) => {
  //   if (confidence >= 0.8) return 'High Confidence';
  //   if (confidence >= 0.6) return 'Medium Confidence';
  //   return 'Low Confidence';
  // };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 10,
      },
    },
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-8 fade-in">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gradient mb-3">🧠 AI Insights</h1>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            Analyzing your health patterns with AI-powered intelligence...
          </p>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="glass-card p-8">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 border-4 border-primary-400/30 border-t-primary-500 rounded-full animate-spin"></div>
              <p className="text-neutral-600 font-medium">Generating insights...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 fade-in">
      {/* Hero Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gradient mb-3">🧠 AI Insights</h1>
        <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
          AI-powered analysis of your symptoms, triggers, and patterns to help you better understand your IBD journey.
        </p>
      </div>

      {/* AI Insights Section */}
      <div className="glass-card-strong p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gradient">AI-Powered Analysis</h2>
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="select-glass"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 3 months</option>
            <option value="365">Last year</option>
          </select>
        </div>

        {error ? (
          <div className="glass-card p-6 border-l-4 border-red-400">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 rounded-full">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-800">Unable to Generate Insights</h3>
                <p className="text-red-600 mt-1">{error}</p>
              </div>
            </div>
          </div>
        ) : insights?.message ? (
          <div className="glass-card p-8 border-l-4 border-teal-400">
            <div className="text-center">
              <div className="p-4 bg-teal-100/50 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-neutral-800 mb-2">Keep Logging Your Journey</h3>
              <p className="text-neutral-600 text-lg">{insights.message}</p>
              <div className="mt-6">
                <button 
                  onClick={() => window.location.href = '/log'}
                  className="btn-primary"
                >
                  Log Today's Entry
                </button>
              </div>
            </div>
          </div>
        ) : insights?.insights ? (
          <div className="glass-card p-6 border-l-4 border-primary-400">
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-primary-gradient rounded-full">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-neutral-800 mb-3">Your Personalized Insights</h3>
                <div className="prose prose-neutral max-w-none">
                  <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap">{insights.insights}</p>
                </div>
                <div className="mt-6 p-4 bg-primary-50 rounded-2xl">
                  <div className="flex items-start space-x-3">
                    <div className="p-1 bg-primary-100 rounded-full">
                      <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-sm text-primary-700">
                      <strong>Remember:</strong> These insights are generated by AI based on your logged data. Always consult with your healthcare provider for medical decisions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Food Correlation Chart */}
        <div className="glass-card p-6">
          <h2 className="text-xl font-bold text-gradient mb-4">
            Food Category Analysis
          </h2>
          <p className="text-sm text-neutral-600 mb-4">
            Visual analysis of how different food categories may correlate with your symptoms
          </p>
          {correlationData ? (
            <div className="relative h-64">
              <Bar data={correlationData} options={chartOptions} />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-center">
              <div>
                <div className="p-4 bg-primary-100/50 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <p className="text-neutral-600">More data needed for analysis</p>
              </div>
            </div>
          )}
        </div>

        {/* Symptom Trends */}
        <div className="glass-card p-6">
          <h2 className="text-xl font-bold text-gradient mb-4">
            Recent Symptom Trends
          </h2>
          <p className="text-sm text-neutral-600 mb-4">
            Track your symptom patterns over time
          </p>
          {trendData ? (
            <div className="relative h-64">
              <Line data={trendData} options={chartOptions} />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-center">
              <div>
                <div className="p-4 bg-teal-100/50 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <p className="text-neutral-600">Keep logging to see trends</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="glass-card p-6">
        <h2 className="text-xl font-bold text-gradient mb-6">Take Action</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button 
            onClick={() => window.location.href = '/log'}
            className="glass-card p-4 text-left hover-lift group border-l-4 border-primary-400"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-gradient rounded-full">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-800">Log Today's Entry</h3>
                <p className="text-sm text-neutral-600">Record symptoms and meals</p>
              </div>
            </div>
          </button>
          
          <button 
            onClick={() => window.location.href = '/appointments'}
            className="glass-card p-4 text-left hover-lift group border-l-4 border-teal-400"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-800">Schedule Appointment</h3>
                <p className="text-sm text-neutral-600">Plan your next healthcare visit</p>
              </div>
            </div>
          </button>
        </div>
        
        <div className="mt-6 p-4 bg-gradient-to-r from-primary-50 to-teal-50 rounded-2xl">
          <div className="flex items-start space-x-3">
            <div className="p-1 bg-primary-100 rounded-full">
              <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-primary-700">
              <strong>Tip:</strong> The more consistently you log entries, the more accurate and helpful your AI insights become.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Insights;